import { CronExpressionParser } from "cron-parser";

/**
 * Consolidation schedule grammar — parsing, validation, and description.
 *
 * Browser-safe by construction: no Node built-ins. The cadence-planning
 * helpers (nextHeartbeatAt / nextConsolidationAt / previewNextRuns) live in
 * ./scheduleParser, which layers deterministic node:crypto jitter on top and
 * re-exports this module for server-side consumers.
 *
 * Three accepted forms:
 *
 *   interval:<seconds>                         repeating fixed interval
 *   daily:HH:MM[,HH:MM...][@IANA/Zone]         one-or-more wall-clock slots per day
 *                                              (timezone defaults to UTC)
 *   cron:<5-field-expression>                  standard cron (UTC)
 *
 * Rationale:
 *   - interval: fits heartbeat-style "every N seconds of uptime" actors
 *   - daily:    fits preset digest actors with explicit local slots
 *   - cron:     covers anything else (weekly, first-of-month, weekday-only, etc.)
 */

export const MIN_CONSOLIDATION_INTERVAL_S = 60;
export const MIN_HEARTBEAT_INTERVAL_S = 5;
export const MAX_HEARTBEAT_INTERVAL_S = 3600;

export interface DailySlot {
  hour: number;
  minute: number;
}

export type ConsolidationSchedule =
  | { kind: "interval"; seconds: number }
  | {
      kind: "daily";
      slots: readonly DailySlot[];
      /** IANA zone. Defaults to "UTC" when not specified. */
      timezone: string;
    }
  | { kind: "cron"; expression: string };

export class InvalidScheduleError extends Error {
  constructor(
    public readonly raw: string,
    message: string,
  ) {
    super(`Invalid consolidation schedule '${raw}': ${message}`);
    this.name = "InvalidScheduleError";
  }
}

function parseDailySlot(raw: string): DailySlot {
  const match = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    throw new Error(`invalid slot '${raw}' (expected HH:MM)`);
  }
  const hour = Number.parseInt(match[1], 10);
  const minute = Number.parseInt(match[2], 10);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    throw new Error(`slot '${raw}' out of range`);
  }
  return { hour, minute };
}

function validateTimezone(tz: string): void {
  try {
    // Constructor throws on invalid timezone; the instance itself is unused.
    // oxlint-disable-next-line eslint/no-new
    new Intl.DateTimeFormat("en-CA", { timeZone: tz });
  } catch {
    throw new Error(`unknown IANA timezone: ${tz}`);
  }
}

/**
 * Parse a consolidation schedule string. Returns null on null/empty input.
 * Throws {@link InvalidScheduleError} on malformed input.
 */
export function parseConsolidationSchedule(
  raw: string | null | undefined,
): ConsolidationSchedule | null {
  if (raw == null) {
    return null;
  }
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }

  const intervalMatch = trimmed.match(/^interval:(\d+)$/);
  if (intervalMatch) {
    const seconds = Number.parseInt(intervalMatch[1], 10);
    if (!Number.isFinite(seconds) || seconds < MIN_CONSOLIDATION_INTERVAL_S) {
      throw new InvalidScheduleError(
        raw,
        `interval must be >= ${MIN_CONSOLIDATION_INTERVAL_S}s`,
      );
    }
    return { kind: "interval", seconds };
  }

  if (trimmed.startsWith("daily:")) {
    const body = trimmed.slice("daily:".length);
    const atIdx = body.lastIndexOf("@");
    const timesPart = atIdx === -1 ? body : body.slice(0, atIdx);
    const timezone = atIdx === -1 ? "UTC" : body.slice(atIdx + 1);
    if (timesPart.length === 0) {
      throw new InvalidScheduleError(raw, "no daily slots given");
    }
    try {
      validateTimezone(timezone);
      const slots = timesPart
        .split(",")
        .map((part) => parseDailySlot(part.trim()));
      // Canonicalize ordering so equal schedules compare equal on round-trip.
      const sorted = [...slots].toSorted(
        (a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute),
      );
      return { kind: "daily", slots: sorted, timezone };
    } catch (error) {
      throw new InvalidScheduleError(
        raw,
        error instanceof Error ? error.message : String(error),
      );
    }
  }

  if (trimmed.startsWith("cron:")) {
    const expression = trimmed.slice("cron:".length).trim();
    if (expression === "") {
      throw new InvalidScheduleError(raw, "cron expression is empty");
    }
    try {
      CronExpressionParser.parse(expression, { tz: "UTC" });
    } catch (error) {
      throw new InvalidScheduleError(
        raw,
        `invalid cron: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
    return { expression, kind: "cron" };
  }

  throw new InvalidScheduleError(
    raw,
    "expected 'interval:<seconds>', 'daily:HH:MM[@tz]', or 'cron:<expr>'",
  );
}

/** Returns true if the schedule string parses cleanly. */
export function isValidConsolidationSchedule(raw: string): boolean {
  try {
    return parseConsolidationSchedule(raw) != null;
  } catch {
    return false;
  }
}

/** Human-readable description of a schedule for UI previews / log lines. */
export function describeConsolidationSchedule(
  schedule: ConsolidationSchedule,
): string {
  if (schedule.kind === "interval") {
    const s = schedule.seconds;
    if (s < 60) {
      return `every ${s}s`;
    }
    if (s < 3600) {
      return `every ${Math.round(s / 60)}m`;
    }
    if (s < 86_400) {
      const h = s / 3600;
      return Number.isInteger(h) ? `every ${h}h` : `every ${h.toFixed(1)}h`;
    }
    const d = s / 86_400;
    return Number.isInteger(d) ? `every ${d}d` : `every ${d.toFixed(1)}d`;
  }
  if (schedule.kind === "cron") {
    return `cron (UTC): ${schedule.expression}`;
  }
  const times = schedule.slots
    .map(
      (s) =>
        `${s.hour.toString().padStart(2, "0")}:${s.minute.toString().padStart(2, "0")}`,
    )
    .join(", ");
  return `daily at ${times} ${schedule.timezone}`;
}

/** Validate a heartbeat interval (seconds). Returns error message or null. */
export function validateHeartbeatIntervalS(n: number): string | null {
  if (!Number.isFinite(n) || !Number.isInteger(n)) {
    return "heartbeat interval must be an integer number of seconds";
  }
  if (n < MIN_HEARTBEAT_INTERVAL_S) {
    return `heartbeat interval must be >= ${MIN_HEARTBEAT_INTERVAL_S}s`;
  }
  if (n > MAX_HEARTBEAT_INTERVAL_S) {
    return `heartbeat interval must be <= ${MAX_HEARTBEAT_INTERVAL_S}s`;
  }
  return null;
}
